import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Utility function to merge Tailwind CSS classes intelligently.
 * Combines clsx for conditional class joining with twMerge for Tailwind
 * class conflict resolution. Ensures proper class precedence and removes
 * conflicting Tailwind utilities.
 * 
 * @param {...ClassValue[]} inputs - Class values to merge (strings, objects, arrays, etc.)
 * @returns {string} Merged and deduplicated class string
 * 
 * @example
 * ```tsx
 * // Basic usage
 * cn("px-4", "py-2", "bg-blue-500")
 * // Returns: "px-4 py-2 bg-blue-500"
 * 
 * // Conditional classes
 * cn("base-class", isActive && "active-class", isDisabled && "disabled-class")
 * 
 * // Resolves Tailwind conflicts
 * cn("px-4", "px-8")
 * // Returns: "px-8" (px-8 overrides px-4)
 * 
 * // With objects
 * cn({ "text-red": isError, "text-green": isSuccess })
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
